import * as THREE from 'three';
import type { AudioEngine } from './AudioEngine';
import {
  CUE_SFX_DEATH,
  CUE_SFX_DILOPH_SPIT,
  CUE_SFX_DILOPH_TELL,
  CUE_SFX_PULSE,
  CUE_SFX_SPEAR,
  synthesizeDeathVeil,
  synthesizeDilophSpit,
  synthesizeDilophTell,
  synthesizePulseStinger,
  synthesizeSpearSwing,
} from './Beds';

export type CueId = 'diloph-tell' | 'diloph-spit' | 'spear' | 'death' | 'pulse';

const CUE_GAIN: Record<CueId, number> = {
  'diloph-tell': 0.24,
  'diloph-spit': 0.3,
  spear: 0.22,
  death: 0.2,
  pulse: 0.18,
};

const CUE_NAME: Record<CueId, string> = {
  'diloph-tell': CUE_SFX_DILOPH_TELL,
  'diloph-spit': CUE_SFX_DILOPH_SPIT,
  spear: CUE_SFX_SPEAR,
  death: CUE_SFX_DEATH,
  pulse: CUE_SFX_PULSE,
};

/** One-shots on the existing listener graph. Diloph tells are positional. */
export class CueDirector {
  readonly names = CUE_NAME;

  play(engine: AudioEngine, id: CueId, at?: THREE.Object3D): void {
    if (!engine.unlocked) {
      return;
    }
    const buffer = bufferFor(engine.ctx, id);
    const gain = CUE_GAIN[id];
    if (at) {
      playPositional(engine, buffer, at, gain);
      return;
    }
    const src = engine.ctx.createBufferSource();
    const amp = engine.ctx.createGain();
    amp.gain.value = gain;
    src.buffer = buffer;
    src.connect(amp);
    amp.connect(engine.buses.sfx);
    src.start();
  }
}

function bufferFor(ctx: BaseAudioContext, id: CueId): AudioBuffer {
  switch (id) {
    case 'diloph-tell':
      return synthesizeDilophTell(ctx);
    case 'diloph-spit':
      return synthesizeDilophSpit(ctx);
    case 'spear':
      return synthesizeSpearSwing(ctx);
    case 'death':
      return synthesizeDeathVeil(ctx);
    case 'pulse':
      return synthesizePulseStinger(ctx);
  }
}

function playPositional(engine: AudioEngine, buffer: AudioBuffer, at: THREE.Object3D, gain: number): void {
  const voice = new THREE.PositionalAudio(engine.listener);
  voice.setBuffer(buffer);
  voice.setRefDistance(5);
  voice.setRolloffFactor(1.15);
  voice.setVolume(gain);
  at.add(voice);
  voice.play();
  const src = voice.source;
  const drop = (): void => {
    voice.removeFromParent();
    voice.disconnect();
  };
  if (src) {
    src.addEventListener('ended', drop);
    return;
  }
  window.setTimeout(drop, Math.ceil(buffer.duration * 1000) + 40);
}

export const cues = new CueDirector();
